/**
 * TriCorps 360 · Help documents registry.
 *
 * Single source of truth for the Help Center and the per-module Help drawer.
 * Playbook PDFs are NOT public: they live in backend/playbook_assets and are
 * served only to signed-in users via GET /api/help/playbooks/{id} (id below is
 * the slug). Open/download them through openPlaybook() (token-authenticated
 * blob fetch) — never a plain <a href>. The `file` field is retained only for
 * reference/labelling and is no longer fetched.
 *
 * `modules` — where the contextual Help drawer surfaces this doc. Every entry
 *            is a keyword the calling page passes to <HelpButton module="xxx">.
 *            One doc can belong to multiple modules; the Signing In guide is
 *            everywhere ("all") because every screen is post-login.
 */
export const HELP_DOCS = [
  {
    id: 'signing-in',
    title: 'Signing In to TriCorps 360',
    description: 'One-page guide covering Microsoft sign-in, sign-out, and what to do if login fails.',
    category: 'Getting Started',
    file: '/TriCorps360-Signing-In.pdf',
    updated: '2026-10-02',
    modules: ['all'],
  },
  {
    id: 'purchase-orders',
    title: 'Purchase Orders Playbook',
    description: 'Create, send, receive, and close vendor POs — with samples of the vendor email and the PO PDF.',
    category: 'AssetHub',
    file: '/TriCorps360-Purchase-Orders.pdf',
    updated: '2026-10-02',
    modules: ['purchase-orders'],
  },
  {
    id: 'uniform-requisitions',
    title: 'Uniform Requisitions Playbook',
    description: 'Requesting, issuing, and archiving uniform issuances with digital signatures.',
    category: 'AssetHub',
    file: '/TriCorps360-Uniform-Requisitions.pdf',
    updated: '2026-10-02',
    modules: ['requests'],
  },
  {
    id: 'evaluations-beta',
    title: 'Evaluations Guide',
    description: 'Part 1 — the evaluation standards: scope, deadlines, the 1–5 scale, the 2.75 standard, failed evaluations and employee rights. Part 2 — the step-by-step module walkthrough.',
    category: 'People & Places',
    file: '/TriCorps360-Evaluations-Beta-Guide.pdf',
    updated: '2026-10-13',
    modules: ['evaluations'],
  },
  {
    id: 'gsoc-operator',
    title: 'GSOC Operator Playbook',
    description: 'Every screen an operator touches, one step at a time — the shift briefing, logging an entry, filing a correction, the SOP library, and the contact directory and call trees.',
    category: 'GSOC',
    file: '/TriCorps360-GSOC-Operator-Playbook.pdf',
    updated: '2026-10-02',
    modules: ['gsoc'],
  },
  {
    id: 'gsoc-manager',
    title: 'GSOC Manager Playbook',
    description: 'Running the GSOC: clients and sites, activity types, pass-on notes, the acknowledgement record, scheduled client reports, log integrity, the contact directory and call trees, and keeping the SOP library current.',
    category: 'GSOC',
    file: '/TriCorps360-GSOC-Manager-Playbook.pdf',
    updated: '2026-10-02',
    modules: ['gsoc', 'gsoc_config'],
  },
  {
    id: 'timesheets-manager',
    title: 'Timesheets Manager Playbook',
    description: 'The weekly timesheet cycle end to end: the weekly status view, downloading and uploading the workbook, submitting, approving or rejecting, the My Work queue, direct self-approval when you are authorized, approved-hours snapshots, and the shareable schedule/job/timesheet deep links.',
    category: 'People & Places',
    file: '/TriCorps360-Timesheets-Manager-Playbook.pdf',
    updated: '2026-06-01',
    modules: ['timesheets', 'scheduling'],
  },
  {
    id: 'sar-payroll',
    title: 'Payroll SAR Playbook',
    description: 'For payroll: open Finance → Payroll Parallel Test. Pay periods are the 1st–15th and the 16th–month end; schedules lock on the 6th and 21st. Pull WinTeam\'s Scheduling Activity Report after the 6th (covers the 16th–end of the prior month) and after the 21st (covers the 1st–15th), upload it on the SAR Import tab, then use the Setup, Overtime Parity, Hours Comparison and Gross Pay Parity tabs. Setup holds pay periods, bulk pay groups, suggest pay groups, classifications, officer pay rates, extra pay (via the payroll API today) and current assignments. Gross Pay Parity (v4.36.0): on the Final import, pick the overtime method (1.5× actual rate, or the FLSA weighted average) and TriCorps compares each employee\'s regular and overtime gross pay against WinTeam\'s, per employee and pay period, with a Rate Parity list of rows whose rate differs. Read-only analysis — it never changes schedules, timesheets, pay, invoices, or the ledger.',
    category: 'Finance',
    file: '/TriCorps360-SAR-Payroll-Playbook.pdf',
    updated: '2026-10-22',
    modules: ['payroll_import'],
    requiresCap: 'payroll_import',
  },
];

// Ordered list of category filters used by the Help Center chip row.
export const HELP_CATEGORIES = [
  'All',
  'Getting Started',
  'AssetHub',
  'People & Places',
  'GSOC',
  'Finance',
];

// Return the docs relevant to a given module key. "all"-scoped docs always
// appear too so managers can always find Signing In from anywhere.
export function docsForModule(moduleKey) {
  return HELP_DOCS.filter(d => d.modules.includes(moduleKey) || d.modules.includes('all'));
}

// Filter out docs the user is not entitled to see. A doc with `requiresCap`
// (e.g. the Payroll SAR Playbook) only appears for someone who can see that
// module, so SAR help never shows to managers or other non-payroll staff.
export function visibleDocs(docs, perms) {
  return docs.filter(d => !d.requiresCap || Boolean(perms?.canModule?.(d.requiresCap)));
}

// Open/download a playbook through the AUTH-GATED backend route. The PDFs are no
// longer in /public, so a plain <a href> (which can't send the bearer token)
// would 404 — fetch as a blob with the api client instead, which attaches the
// token, then open or download the blob.
import api from '../lib/api';

export async function openPlaybook(doc, { download = false } = {}) {
  const res = await api.get(`/help/playbooks/${doc.id}`, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  if (download) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.title.replace(/[^a-z0-9]+/gi, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(url, '_blank', 'noopener');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
